import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { Form } from "@/components/form/form";
import { FormSelect, FormTextField } from "@/components/form/fields";
import { SmtpSection } from "@/components/settings/smtp-section";
import { webhooksApi, type webhookSchema } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/notifications")({
  component: NotificationsSettings,
});

type Webhook = z.infer<typeof webhookSchema>;

const schema = z.object({
  name: z.string().min(2, "Name the destination"),
  url: z.string().url("Must be an https URL"),
  format: z.string().min(1),
});

const FORMATS = [
  { value: "SLACK", label: "Slack" },
  { value: "GENERIC", label: "Generic JSON" },
  { value: "PAGERDUTY", label: "PagerDuty" },
];

function NotificationsSettings() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = webhooksApi.useList();
  const m = webhooksApi.useMutations();
  const [creating, setCreating] = useState(false);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", url: "", format: "SLACK" },
  });

  const columns: Column<Webhook>[] = [
    { id: "name", header: "Name", cell: (r) => r.name ?? "—" },
    { id: "url", header: "URL", cell: (r) => <MonoText>{r.url ?? "—"}</MonoText> },
    { id: "format", header: "Format", cell: (r) => r.format ?? "GENERIC", nowrap: true },
    {
      id: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled === false ? (
          <Chip size="small" label="Disabled" />
        ) : (
          <Chip size="small" color="success" label="Active" />
        ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <IconButton
          size="small"
          color="error"
          aria-label="Delete webhook"
          onClick={async (e) => {
            e.stopPropagation();
            if (await confirm({ title: `Delete "${r.name ?? r.url}"?`, destructive: true })) {
              await m.remove.mutateAsync(r.id);
              toast.success("Webhook deleted");
            }
          }}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      ),
    },
  ];

  async function onCreate(values: z.infer<typeof schema>) {
    await m.create.mutateAsync({ ...values, enabled: true });
    toast.success("Webhook added");
    setCreating(false);
    form.reset();
  }

  return (
    <PageContainer>
      <PageHeader
        title="Notifications"
        description="Send pipeline failure and anomaly alerts to Slack, PagerDuty or a custom endpoint."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            Add webhook
          </Button>
        }
      />
      <Card sx={{ mb: 4 }}>
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          empty={
            <EmptyState
              title="No alert destinations"
              description="Add a webhook to be notified when pipelines fail or anomalies fire."
            />
          }
        />
      </Card>

      <SmtpSection />

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add alert webhook</DialogTitle>
        <Form form={form} onSubmit={onCreate}>
          <DialogContent>
            <Stack spacing={2}>
              <FormTextField control={form.control} name="name" label="Name" required />
              <FormTextField
                control={form.control}
                name="url"
                label="Webhook URL"
                required
                placeholder="https://hooks.slack.com/services/…"
              />
              <FormSelect control={form.control} name="format" label="Format" options={FORMATS} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
              Add
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </PageContainer>
  );
}
